import {
  AlertCircle,
  Bath,
  Bed,
  Bot,
  Building,
  Calendar,
  CalendarClock,
  Car,
  Check,
  ChevronDown,
  Clock,
  Copy,
  DollarSign,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Hash,
  Home,
  Layers,
  Link2,
  Loader2,
  Mail,
  Map as MapIcon,
  MapPin,
  PawPrint,
  Pencil,
  Phone,
  RefreshCw,
  Ruler,
  ShieldCheck,
  Tag,
  UserCog,
  Wallet,
  Webhook,
} from "lucide-react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { useContext, useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";
import { Collapse } from "@/components/ui/collapse";
import { JsonBlock } from "./JsonBlock";
import AssignmentRationaleDetail from "@/components/leads/chat/AssignmentRationaleDetail";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import posthog from "@/lib/posthog";
import { colorFromName, formatCOP, initials } from "@/lib/format";
import { contactDisplay, leadDisplayName } from "@/lib/contact";
import { trpc } from "@/trpc";
import type { RouterOutputs } from "@/trpc";
import type { PipelinePosition } from "@/contexts/PipelineContext";
import UserContext from "@/contexts/UserContext";
import {
  canManageRentGuarantee,
  canMoveLeadToPipelinePosition,
  isInRentGuaranteeFunnel,
  pipelineOffersRentGuarantee,
} from "@/utils/permissions";
import RentGuaranteeRequestForm from "./RentGuaranteeRequestForm";
import RentGuaranteeCosignersEditor, {
  type Cosigner,
} from "./RentGuaranteeCosignersEditor";
import type { ActivityItem } from "@/components/leads/chat/ConversationTimeline";
import { activityIcon, activityLabel } from "./ChatTimeline";
import AgenteIaControl from "./AgenteIaControl";
import LeadTagsEditor from "@/components/leadTags/LeadTagsEditor";
import LeadNotesSection, { type LeadNote } from "@/components/leadNotes/LeadNotesSection";
import { MOVABLE_STAGES, sourceLabel } from "@/components/tablero/types";
import type { TicketPosition } from "@/components/tablero/solicitudes/types";

type LeadDetail = RouterOutputs["leads"]["get"];
type Appointment = RouterOutputs["appointments"]["list"]["data"][number];
type CrmSyncLogItem = RouterOutputs["leads"]["listCrmSyncLogs"][number];
type Activity = ActivityItem["activity"];
type Solicitud = RouterOutputs["serviceRequests"]["listByLead"]["data"][number];
type Call = RouterOutputs["calls"]["list"][number];
// Null when the lead has no conversation yet (conversations.get returns null).
type Conversation = RouterOutputs["conversations"]["get"];
type ListingRequest = RouterOutputs["listingRequest"]["list"]["data"][number];

const APPOINTMENT_STATUS_NOTES: Record<string, string> = {
  cancelled: "cancelada",
  no_show: "no asistió",
};

const LISTING_TYPE_LABELS: Record<string, string> = {
  rent: "Arriendo",
  sale: "Venta",
  rent_and_sale: "Arriendo y venta",
};

/** Property types are stored in English; captación renders them in Spanish, the
 *  same map CaptacionTab uses in the inbox. Covers the `propertyTypes` enum. */
const PROPERTY_TYPE_LABELS: Record<string, string> = {
  studio: "Apartaestudio",
  apartment: "Apartamento",
  warehouse: "Bodega",
  house: "Casa",
  medical_office: "Consultorio",
  building: "Edificio",
  farm: "Finca",
  room: "Habitación",
  hotel: "Hotel",
  commercial_space: "Local comercial",
  lot: "Lote",
  office: "Oficina",
  parking: "Parqueadero",
};

// MUST stay in sync with LeadRentGuaranteeDocumentType in packages/agent/src/core/lead/types.ts.
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  cc: "Cédula de ciudadanía",
  ce: "Cédula de extranjería",
  nit: "NIT",
  ps: "Pasaporte",
};

const CALL_STATUS_LABELS: Record<string, string> = {
  queued: "En cola",
  scheduled: "Programada",
  ringing: "Sonando",
  "in-progress": "En progreso",
  forwarding: "Transferida",
  ended: "Finalizada",
  cancelled: "Cancelada",
};

/** VAPI reports an unanswered call the same way whether a person declined or an
 *  answering machine picked up; the transcript is the only thing that separates
 *  them. Same keyword list the inbox CallCard uses. */
const VOICEMAIL_KEYWORDS = [
  "contestador",
  "buzón de voz",
  "buzon de voz",
  "deja tu mensaje",
  "correo de voz",
];

const endedReasonTag = (call: Call): string | undefined => {
  if (call.endedReason !== "customer-did-not-answer") return undefined;
  const transcript = call.transcript?.toLowerCase() ?? "";
  return VOICEMAIL_KEYWORDS.some((keyword) => transcript.includes(keyword))
    ? "Buzón de voz"
    : "No contestó";
};

const formatCallDuration = (durationMinutes?: number | null): string => {
  if (!durationMinutes) return "-";
  const minutes = Math.floor(durationMinutes);
  const seconds = Math.round((durationMinutes - minutes) * 60);
  return `${minutes} min${minutes !== 1 ? "s" : ""}${seconds > 0 ? ` ${seconds} seg` : ""}`;
};

/** VAPI transcripts arrive as one string delimited by "User:" / "AI:" turns. */
const parseTranscript = (transcript?: string | null): { speaker: "User" | "AI"; text: string }[] => {
  if (!transcript) return [];
  return transcript
    .split(/User:|AI:/)
    .slice(1)
    .map((text, index) => ({ speaker: index % 2 === 0 ? "User" : "AI", text: text.trim() }) as const);
};

// leads.get exposes profile as an untyped passthrough; this mirrors the
// entity's profile map (core/lead/repository.ts) for the fields we render.
// Field names and shapes are the entity's, NOT the inbox panel's older guesses:
// bedrooms/bathrooms/garages are scalars, and it is sqMeters + isPetFriendly.
type LeadProfile = {
  price?: { min?: number; max?: number };
  propertyTypes?: string[];
  neighborhoods?: string[];
  cities?: string[];
  bedrooms?: number;
  bathrooms?: number;
  garages?: number;
  isPetFriendly?: boolean;
  sqMeters?: { min?: number; max?: number };
  socialStratum?: string[];
  listingType?: string;
  propertyCode?: string;
  daysUntilMoveIn?: number;
};

// leads.get exposes rentGuarantee as an untyped passthrough too; this mirrors the entity's
// rentGuarantee map (core/lead/types.ts). Palomma-owned — merchants cannot add fields to it,
// unlike profile, so this list is the whole shape rather than "the fields we render".
type LeadRentGuarantee = {
  habeasDataConfirmed?: boolean;
  documentNumber?: string;
  // Absent only on rows written before the field existed; both request paths set it now.
  documentType?: Cosigner["documentType"];
  income?: number;
  incomeAssessment?: {
    // Absent on rows before the advisor path existed, which all came from a listing.
    canonSource?: "listing" | "advisor" | "tenant";
    // Present iff the canon came from a listing.
    propertyCode?: string;
    monthlyRent: number;
    ratio: number;
    meetsRequirement: boolean;
    assessedAt: string;
  };
  requestedAt?: string;
  // The portal user who filed it; absent when the AI's tool did.
  requestedBy?: string;
  // Deudores solidarios, recorded by hand by Palomma staff during the estudio.
  cosigners?: Cosigner[];
  cosignersUpdatedBy?: string;
  cosignersUpdatedAt?: string;
};

/**
 * Every group sits on its own Counter White surface with a hairline edge, over the
 * panel's page wash. Stacked headings alone let a dense, heterogeneous panel blur
 * into one column of text — the surface break is what makes the groups scannable.
 */
const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="rounded-lg border border-neutral-300 bg-surface p-4">
    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">{title}</h3>
    {children}
  </section>
);

const Row = ({
  Icon,
  label,
  value,
}: {
  Icon: ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
}) => (
  <div className="flex gap-3 text-sm">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-neutral-600" />
    <div className="min-w-0 flex-1">
      <div className="text-xs text-neutral-600">{label}</div>
      <div className="break-words text-neutral-900">{value}</div>
    </div>
  </div>
);

/**
 * The enquired property from the portal email. Hydrated to an address + price + availability so
 * an advisor can tell at a glance when a lead landed on them pointing at stale inventory — the
 * "No disponible" badge is the whole reason a lead can read oddly against its conversation. Falls
 * back to the raw code when the code no longer resolves (retired/foreign listing), so the advisor
 * still sees what the email named.
 */
const EnquiredListingRow = ({
  listing,
  fallbackCode,
}: {
  listing?: LeadDetail["enquiredListing"];
  fallbackCode?: string;
}) => {
  if (!listing) {
    if (!fallbackCode) return null;
    return <Row Icon={Tag} label="Inmueble de interés" value={fallbackCode} />;
  }

  const location = [listing.neighborhood, listing.city].filter(Boolean).join(" · ");

  return (
    <div className="flex gap-3 text-sm">
      <Tag className="mt-0.5 h-4 w-4 shrink-0 text-neutral-600" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-600">Inmueble de interés</span>
          {listing.status === "unavailable" && (
            <Badge variant="destructive" className="px-1.5 py-0 text-[10px]">
              No disponible
            </Badge>
          )}
        </div>
        <div className="break-words text-neutral-900">{listing.code}</div>
        {location && <div className="break-words text-neutral-600">{location}</div>}
        {listing.price !== undefined && (
          <div className="text-neutral-600">{formatCOP(listing.price)}</div>
        )}
        {listing.url && (
          <a
            href={listing.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-900 hover:opacity-70"
          >
            <ExternalLink className="size-3.5" />
            Ver inmueble
          </a>
        )}
      </div>
    </div>
  );
};

/**
 * Why no document exists. Spelled out rather than left blank: an asesor who moves a card onto a
 * fianza position and finds nothing there has no way to tell whether it is still generating,
 * failed, or was never going to happen — and will report it as a bug.
 *
 * Only the cause, so the sentence can name whichever document is missing. Saying "certificado" on
 * the approval would name the wrong one.
 */
const UNAVAILABLE_REASON: Record<string, string> = {
  "no-document-number": "falta la cédula del solicitante",
  "no-income": "falta el ingreso declarado",
  "below-minimum-ratio": "los ingresos no alcanzan 1.7x el canon",
  "income-too-low": "el ingreso declarado no alcanza para un canon",
};

/**
 * Copy for each document. `action` is the button label, kept short enough to hold one line in a
 * 320px panel — the full name wrapped, which centred the text and left the icon stranded from it.
 * `noun` carries its article so the status line and the toasts read as Spanish sentences.
 *
 * The approval is deliberately not a "certificado": the design reserves that word for the
 * certificate issued once the contract is entered, a later document than either of these.
 */
const CERTIFICATE_COPY = {
  preApproved: {
    action: "Descargar preaprobación",
    noun: "el certificado de preaprobación",
    validUntil: "Certificado vigente hasta",
  },
  approved: {
    action: "Descargar aprobación",
    noun: "el documento de aprobación",
    validUntil: "Aprobación vigente hasta",
  },
} as const;

const RentGuaranteeCertificateBlock = ({
  leadId,
  kind,
}: {
  leadId: string;
  kind: keyof typeof CERTIFICATE_COPY;
}) => {
  const utils = trpc.useUtils();
  const [isDownloading, setIsDownloading] = useState(false);

  const { data } = trpc.leads.getRentGuaranteeCertificate.useQuery(
    { leadId, kind },
    // The document is rendered off a queue, so it lands a few seconds after the card moves. Polling
    // stops as soon as it does — every other state is settled.
    { refetchInterval: (result) => (result?.status === "pending" ? 5000 : false) }
  );

  if (!data || data.status === "none") return null;

  // The URL is signed and expires, so it is fetched on click rather than carried in the payload.
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const result = await utils.leads.getRentGuaranteeCertificateUrl.fetch({ leadId, kind });
      if (result?.url) window.open(result.url, "_blank", "noopener,noreferrer");
      else toast.error(`Aún no está disponible ${CERTIFICATE_COPY[kind].noun}.`);
    } catch (error) {
      toast.error(`No pudimos abrir ${CERTIFICATE_COPY[kind].noun}.`);
      console.error("getRentGuaranteeCertificateUrl failed", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {kind === "preApproved" && data.status !== "pending" && "number" in data && data.number && (
        <Row Icon={Hash} label="Número de radicado del estudio" value={data.number} />
      )}

      {data.status === "issued" && (
        <>
          <Row
            Icon={CalendarClock}
            label={CERTIFICATE_COPY[kind].validUntil}
            value={dayjs(data.expiresAt).format("DD/MM/YYYY")}
          />
          {/* The filled variant, full width with a download icon — the same shape BillableReportCard
              uses for its download, and the reason an asesor reported not recognising the previous
              inline text-link as something to click. `outline` would have been wrong twice over: it
              reads as the secondary action everywhere else in this portal.
              The focus ring is widened and offset because the base 1px ring is the same purple as
              this button's fill, which leaves keyboard focus invisible on the filled variant. */}
          <Button
            onClick={handleDownload}
            disabled={isDownloading}
            aria-busy={isDownloading}
            className="min-h-11 w-full focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {isDownloading ? (
              // The label carries the state on its own, so stopping the spin costs nothing.
              <Loader2 className="animate-spin motion-reduce:animate-none" />
            ) : (
              <Download />
            )}
            {isDownloading ? "Abriendo…" : CERTIFICATE_COPY[kind].action}
          </Button>
        </>
      )}

      {data.status === "pending" && (
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <Loader2 className="size-4 shrink-0 animate-spin motion-reduce:animate-none" />
          Generando {CERTIFICATE_COPY[kind].noun}…
        </div>
      )}

      {data.status === "unavailable" && (
        // Icon plus text, never colour alone — the reason has to survive for anyone who cannot
        // distinguish the states.
        <div className="flex gap-2 text-sm text-neutral-600">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>
            {UNAVAILABLE_REASON[data.reason]
              ? `No se emitió ${CERTIFICATE_COPY[kind].noun}: ${UNAVAILABLE_REASON[data.reason]}.`
              : `No se emitió ${CERTIFICATE_COPY[kind].noun} para este lead.`}
          </span>
        </div>
      )}
    </>
  );
};

/** «Juan Perez» y «J. Perez» — the names the stale documents print, as Spanish reads a list. */
const quoteNames = (names: string[]): string => {
  const quoted = names.map((name) => `«${name}»`);
  return quoted.length <= 1
    ? quoted.join("")
    : `${quoted.slice(0, -1).join(", ")} y ${quoted[quoted.length - 1]}`;
};

/** How long the block waits for a requested re-render to land before giving the button back. */
const REGENERATION_TIMEOUT_MS = 2 * 60_000;

type CertificateStatus = RouterOutputs["leads"]["getRentGuaranteeCertificate"];

/**
 * Says when the issued fianza documents are behind the lead, and offers to generate them again.
 *
 * Two ways a document goes stale, and they are detected differently because the certificate row
 * stores one of them and not the other:
 *
 * - The NAME is frozen on the row at issuance, so it is compared directly. Only when the lead has
 *   a name to print — a document reading "No proporcionado" over a nameless lead is not stale.
 * - The COSIGNERS are not on the row at all; they are read off the lead when the PDF renders. So
 *   the test is a timestamp: anything stamped on `cosignersUpdatedAt` after the document was last
 *   rendered (`renderedAt`, which a regeneration moves and the frozen `issuedAt` does not) is not
 *   on the PDF.
 *
 * Both surface the notice at once and a landed regeneration is what clears it. One button for the
 * lead: every issued document is generated again off the lead as it is now.
 */
const RentGuaranteeRegenerateBlock = ({ lead }: { lead: LeadDetail }) => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  // Shares the two certificate blocks' cache entries; while a re-render is in flight this is the
  // one place that polls them.
  const refetchInterval = isRegenerating ? 5000 : false;
  const preApproved = trpc.leads.getRentGuaranteeCertificate.useQuery(
    { leadId: lead.id, kind: "preApproved" },
    { refetchInterval }
  );
  const approved = trpc.leads.getRentGuaranteeCertificate.useQuery(
    { leadId: lead.id, kind: "approved" },
    { refetchInterval }
  );

  const regenerate = trpc.leads.regenerateRentGuaranteeCertificates.useMutation({
    onSuccess: () => setIsRegenerating(true),
    onError: (error) => {
      toast.error("No pudimos pedir la regeneración de los documentos. Intenta de nuevo.");
      console.error("regenerateRentGuaranteeCertificates failed", error);
    },
  });

  const currentName = lead.name?.trim() ?? "";
  const cosignersUpdatedAt = (lead.rentGuarantee as LeadRentGuarantee | undefined)
    ?.cosignersUpdatedAt;

  const issued = [preApproved.data, approved.data].filter(
    (status): status is Extract<CertificateStatus, { status: "issued" }> =>
      status?.status === "issued"
  );
  const staleByName = issued.filter(
    (status) => currentName !== "" && status.applicantName !== currentName
  );
  // ISO-8601 UTC throughout, so a string compare is a time compare.
  const staleByCosigners = issued.filter(
    (status) => !!cosignersUpdatedAt && cosignersUpdatedAt > status.renderedAt
  );
  const stale = issued.filter(
    (status) => staleByName.includes(status) || staleByCosigners.includes(status)
  );

  // Done when nothing reads stale any more. Past the timeout the render is in the DLQ — or the lead
  // no longer qualifies and kept its old document — and the button comes back rather than spinning.
  useEffect(() => {
    if (!isRegenerating) return;
    if (stale.length === 0) {
      setIsRegenerating(false);
      toast.success("Documentos de fianza regenerados.");
      return;
    }
    const timeout = setTimeout(() => {
      setIsRegenerating(false);
      toast.error("No pudimos regenerar los documentos de fianza. Intenta de nuevo.");
    }, REGENERATION_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isRegenerating, stale.length]);

  if (isRegenerating) {
    return (
      <div className="flex items-center gap-2 text-sm text-neutral-600">
        <Loader2 className="size-4 shrink-0 animate-spin motion-reduce:animate-none" />
        Regenerando los documentos de fianza…
      </div>
    );
  }

  if (stale.length === 0) return null;

  const plural = stale.length > 1;
  const from = Array.from(new Set(staleByName.map((status) => status.applicantName)));
  // Names the actual reason rather than a generic "están desactualizados": an asesor deciding
  // whether to regenerate wants to know what changed. Both can be true at once.
  const reasons = [
    from.length > 0
      ? `${plural ? "dicen" : "dice"} ${quoteNames(from)}, pero el lead ahora se llama «${currentName}»`
      : undefined,
    staleByCosigners.length > 0
      ? `no ${plural ? "incluyen" : "incluye"} los deudores solidarios actuales`
      : undefined,
  ].filter((reason): reason is string => reason !== undefined);

  return (
    <div className="space-y-2">
      {/* Icon plus text, never colour alone — same rule as the unavailable state above. */}
      <div className="flex gap-2 text-sm text-neutral-600">
        <AlertCircle className="mt-0.5 size-4 shrink-0" />
        <span>
          {plural ? "Los documentos de fianza" : "El documento de fianza"} {reasons.join(" y ")}.
        </span>
      </div>
      {/* Outline, not filled: downloading the document stays the primary action of this section. */}
      <Button
        variant="outline"
        onClick={() => regenerate.mutate({ leadId: lead.id })}
        disabled={regenerate.isLoading}
        aria-busy={regenerate.isLoading}
        className="min-h-11 w-full"
      >
        {regenerate.isLoading ? (
          <Loader2 className="animate-spin motion-reduce:animate-none" />
        ) : (
          <RefreshCw />
        )}
        {plural ? "Regenerar documentos" : "Regenerar documento"}
      </Button>
    </div>
  );
};

/**
 * The lead's name, editable in place. Enter or ✓ saves, Escape restores, and leaving the field
 * saves too — the same forgiveness the pipeline position rename has, and what an asesor fixing a
 * typo expects from an inline field. Only a changed, non-empty name is written.
 */
const LeadNameEditor = ({
  lead,
  merchantId,
  displayName,
}: {
  lead: LeadDetail;
  merchantId: string;
  displayName: string;
}) => {
  const utils = trpc.useUtils();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  // Refs rather than state because blur, Escape and the ✓ click can all land in the same tick,
  // before a re-render: the first commit must make the second a no-op, and an editor closed by
  // Escape must not be written by the blur its own unmount can fire.
  const isOpenRef = useRef(false);
  const isCommittingRef = useRef(false);
  const updateName = trpc.leads.updateName.useMutation();

  const handleStartEditing = () => {
    setDraft(lead.name ?? "");
    isOpenRef.current = true;
    setIsEditing(true);
  };

  const close = () => {
    isOpenRef.current = false;
    setIsEditing(false);
  };

  const commit = async () => {
    if (!isOpenRef.current || isCommittingRef.current) return;
    const name = draft.trim();
    if (!name || name === lead.name?.trim()) {
      close();
      return;
    }

    isCommittingRef.current = true;
    try {
      await updateName.mutateAsync({ leadId: lead.id, name });
      close();
      // The mutation returned the stored name, so this panel is patched in place; the lists that
      // print the lead in rows it does not own go back to the server.
      utils.leads.get.setData({ merchantId, id: lead.id }, (prev) =>
        prev ? { ...prev, name } : prev
      );
      utils.leads.listActiveByPosition.invalidate();
      utils.leads.searchActiveNonRealtime.invalidate();
      utils.inbox.list.invalidate();
      toast.success("Nombre actualizado");
    } catch (error) {
      toast.error("No pudimos actualizar el nombre. Intenta de nuevo.");
      console.error("updateName failed", error);
    } finally {
      isCommittingRef.current = false;
    }
  };

  if (!isEditing) {
    return (
      <div className="flex items-center gap-1">
        <span className="min-w-0 truncate text-sm font-semibold text-neutral-900">{displayName}</span>
        {/* Sits on the name rather than in the header's action group so it reads as "edit this",
            not as one more thing to do with the lead. */}
        <button
          type="button"
          onClick={handleStartEditing}
          title="Editar nombre"
          aria-label="Editar nombre"
          className="-my-2.5 flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <Pencil className="size-3.5" />
        </button>
      </div>
    );
  }

  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(event) => {
        event.preventDefault();
        void commit();
      }}
    >
      <Input
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            close();
          }
        }}
        maxLength={120}
        aria-label="Nombre del lead"
        placeholder="Nombre del lead"
        className="min-w-0 flex-1"
        disabled={updateName.isLoading}
      />
      <Button
        type="submit"
        variant="ghost"
        size="icon"
        title="Guardar nombre"
        aria-label="Guardar nombre"
        className="min-h-11 min-w-11 shrink-0"
        disabled={updateName.isLoading}
        // Keeps focus on the input so the click is not preceded by a blur commit — one write, not two.
        onPointerDown={(event) => event.preventDefault()}
      >
        {updateName.isLoading ? (
          <Loader2 className="size-3.5 animate-spin motion-reduce:animate-none" />
        ) : (
          <Check className="size-3.5" />
        )}
      </Button>
    </form>
  );
};

/** Key/value line for the metadata group, where an icon per row would be noise. */
const MetaRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="shrink-0 text-neutral-600">{label}</span>
    <span className="min-w-0 break-words text-right text-neutral-900">{value}</span>
  </div>
);

/**
 * Drops the rule segment from a frozen assignment headline
 * ("Asignado a Ana · Regla \"El Poblado\"" → "Asignado a Ana").
 *
 * The ruleset name is shown top level in "Manejado por"; repeating it on every
 * assignment row put the same string twice in one panel. Only `Regla …` segments go —
 * the other causes the headline can carry ("Asesor del inmueble ABC123", "Ya atiende a
 * este cliente") appear nowhere else, so they stay. Operates on the segments rather
 * than the whole string because the headline is frozen text (helpers/
 * assignmentRationaleText.ts) that must never be re-derived here.
 */
const stripRuleSegment = (label: string): string =>
  label
    .split(" · ")
    .filter((segment) => !segment.startsWith("Regla "))
    .join(" · ");

const Chips = ({ values }: { values: string[] }) => (
  <div className="flex flex-wrap gap-1.5">
    {values.map((value, index) => (
      <span
        key={index}
        className="inline-flex items-center rounded-pill bg-neutral-100 px-2.5 py-0.5 text-xs text-neutral-900"
      >
        {value}
      </span>
    ))}
  </div>
);

const CrmLogItem = ({ log }: { log: CrmSyncLogItem }) => {
  const [expanded, setExpanded] = useState(false);
  const isError = log.outcome === "error";

  return (
    <li>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center gap-2 text-left"
      >
        <span
          className={cn("size-2 shrink-0 rounded-full", isError ? "bg-destructive" : "bg-success")}
        />
        <span className="min-w-0 flex-1 truncate text-neutral-900">
          {log.event} · {log.provider}
        </span>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-neutral-600 transition-transform",
            expanded && "rotate-180"
          )}
        />
      </button>
      <div className="pl-4 text-xs text-neutral-600">
        {log.pipelinePositionLabel ? `${log.pipelinePositionLabel} · ` : ""}
        {dayjs(log.createdAt).format("DD/MM/YYYY HH:mm")}
        {log.httpStatus != null ? ` · HTTP ${log.httpStatus}` : ""}
      </div>
      <Collapse isOpen={expanded}>
        <div className="mt-2 flex flex-col gap-2 pl-4">
          {log.endpoint && (
            <div className="break-all font-mono text-xs text-neutral-600">{log.endpoint}</div>
          )}
          {log.errorMessage && (
            <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">
              {log.errorMessage}
            </div>
          )}
          {log.requestPayload != null && (
            <JsonBlock label="Payload enviado" value={log.requestPayload} />
          )}
          {log.responseBody != null && <JsonBlock label="Respuesta" value={log.responseBody} />}
        </div>
      </Collapse>
    </li>
  );
};

/**
 * One voice call. The inbox renders this in a full-width tab, so its transcript is a
 * two-column table; at 380px that would be unreadable, and the raw green/blue/purple
 * palette it uses is a second colour voice the panel does not have. Same information,
 * stacked: chips for direction and outcome, then duration, resumen, and the turns as
 * speaker-over-text rather than table rows.
 */
const CallItem = ({ call, merchantId }: { call: Call; merchantId: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [isLoadingRecording, setIsLoadingRecording] = useState(false);
  const utils = trpc.useUtils();
  const tag = endedReasonTag(call);
  const turns = parseTranscript(call.transcript);

  // The recording lives behind a signed URL that expires, so it is fetched on click
  // rather than embedded in the list payload.
  const handlePlayRecording = async () => {
    setIsLoadingRecording(true);
    try {
      const data = await utils.calls.getRecordingUrl.fetch({ merchantId, callId: call.id });
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error("No pudimos abrir la grabación.");
      console.error("getRecordingUrl failed", error);
    } finally {
      setIsLoadingRecording(false);
    }
  };

  return (
    <li className="rounded-md bg-neutral-100/70 px-3 py-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <Phone className="size-4 shrink-0 text-neutral-600" />
        <span className="rounded-pill bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-900">
          {call.type === "inbound" ? "Entrante" : "Saliente"}
        </span>
        <span
          className={cn(
            "rounded-pill px-2 py-0.5 text-xs font-medium text-neutral-900",
            call.status === "cancelled" ? "bg-danger-light" : "bg-neutral-100"
          )}
        >
          {CALL_STATUS_LABELS[call.status] ?? call.status}
        </span>
        {tag && (
          <span
            className={cn(
              "rounded-pill px-2 py-0.5 text-xs font-medium text-neutral-900",
              tag === "Buzón de voz" ? "bg-warning-light" : "bg-danger-light"
            )}
          >
            {tag}
          </span>
        )}
        {call.status === "cancelled" && call.cancelledReason && (
          <span className="rounded-pill bg-danger-light px-2 py-0.5 text-xs font-medium text-neutral-900">
            {call.cancelledReason}
          </span>
        )}
      </div>

      <div className="mt-1 flex items-center gap-2 text-xs text-neutral-600">
        <span>{dayjs(call.createdAt).format("DD/MM/YYYY HH:mm")}</span>
        <span className="inline-flex items-center gap-1">
          <Clock className="size-3" />
          {formatCallDuration(call.durationMinutes)}
        </span>
      </div>

      {call.analysis?.summary && (
        <p className="mt-1.5 whitespace-pre-wrap break-words text-neutral-900">
          {call.analysis.summary}
        </p>
      )}

      {(call.recordingUrl || call.transcript) && (
        <div className="mt-1.5 flex flex-wrap gap-3">
          {call.recordingUrl && (
            <button
              onClick={handlePlayRecording}
              disabled={isLoadingRecording}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-900 hover:opacity-70 disabled:opacity-50"
            >
              {isLoadingRecording ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Phone className="size-3.5" />
              )}
              Escuchar grabación
            </button>
          )}
          {call.transcript && (
            <button
              onClick={() => setExpanded((value) => !value)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-900 hover:opacity-70"
            >
              <FileText className="size-3.5" />
              Ver transcripción
              <ChevronDown
                className={cn("size-3.5 transition-transform", expanded && "rotate-180")}
              />
            </button>
          )}
        </div>
      )}

      {call.transcript && (
        <Collapse isOpen={expanded}>
          <ul className="mt-2 space-y-2">
            {turns.map((turn, index) => (
              <li key={index}>
                <span
                  className={cn(
                    "rounded-pill px-2 py-0.5 text-xs font-medium text-neutral-900",
                    turn.speaker === "User" ? "bg-neutral-100" : "bg-success-light"
                  )}
                >
                  {turn.speaker === "User" ? "Cliente" : "Paloma"}
                </span>
                <p className="mt-0.5 whitespace-pre-wrap break-words text-neutral-900">
                  {turn.text}
                </p>
              </li>
            ))}
          </ul>
        </Collapse>
      )}

      {!call.transcript && !call.recordingUrl && (
        <p className="mt-1 text-xs italic text-neutral-600">Transcripción no disponible</p>
      )}
    </li>
  );
};

/**
 * One captación request. The inbox stacks seven single-field cards for this; the panel
 * already has a key/value idiom for exactly this shape, so it reuses MetaRow.
 */
const CaptacionItem = ({ request, index }: { request: ListingRequest; index: number }) => (
  <li className="rounded-md bg-neutral-100/70 px-3 py-2">
    <div className="mb-1.5 flex items-center gap-2 font-semibold text-neutral-900">
      <Building className="size-4 shrink-0 text-neutral-600" />
      Solicitud #{index + 1}
    </div>
    <div className="space-y-1.5">
      <MetaRow
        label="Tipo de listado"
        value={LISTING_TYPE_LABELS[request.listingType] ?? request.listingType}
      />
      <MetaRow
        label="Tipo de inmueble"
        value={PROPERTY_TYPE_LABELS[request.propertyType] ?? request.propertyType}
      />
      <MetaRow
        label="Estado"
        value={request.status === "ocupado" ? "Ocupado" : "Desocupado"}
      />
      <MetaRow label="Ciudad" value={request.city} />
      {request.neighborhood && <MetaRow label="Barrio" value={request.neighborhood} />}
      {request.address && <MetaRow label="Dirección" value={request.address} />}
      <MetaRow label="Creada" value={dayjs(request.createdAt).format("DD/MM/YYYY HH:mm")} />
    </div>
  </li>
);

const PerfilPanel = ({
  lead,
  positions,
  onChangeEtapa,
  isMoving,
  appointments,
  activities,
  crmSyncLogs,
  allPositions,
  solicitudes,
  ticketPositions,
  searchedCodes,
  listingRequests,
  calls,
  notes,
  merchantId,
  surface,
  conversation,
}: {
  lead: LeadDetail;
  positions: PipelinePosition[];
  onChangeEtapa: (positionId: string) => void;
  isMoving: boolean;
  appointments: Appointment[];
  activities: Activity[];
  crmSyncLogs: CrmSyncLogItem[];
  allPositions: Array<{ id: string; displayName: string; stage: string }>;
  solicitudes: Solicitud[];
  ticketPositions: TicketPosition[];
  searchedCodes: string[];
  listingRequests: ListingRequest[];
  calls: Call[];
  notes: LeadNote[] | undefined;
  merchantId: string;
  surface: "inbox" | "tablero";
  conversation: Conversation;
}) => {
  const { email } = useContext(UserContext);
  // `phone` carries a BSUID for phoneless senders — never a name, never a number.
  const displayName = leadDisplayName(lead);
  const contact = contactDisplay(lead);
  const profile = (lead.profile ?? {}) as LeadProfile;
  const hasProfile = Object.keys(profile).length > 0;
  const rentGuarantee = lead.rentGuarantee as LeadRentGuarantee | undefined;
  const hasRentGuarantee = Boolean(rentGuarantee && Object.keys(rentGuarantee).length > 0);
  // A lead with no fianza data can be given one from here, but only on a pipeline that can take
  // it somewhere — the endpoint applies the same test. Off such a pipeline the section is hidden
  // entirely, as it always was for leads that never went through a request: an empty "Fianza"
  // card on every lead would be noise.
  const canRequestRentGuarantee = !hasRentGuarantee && pipelineOffersRentGuarantee(positions);
  const fuente = sourceLabel(lead.source);
  const isHumanAssigned = lead.handledBy !== "ai" && Boolean(lead.employeeId);
  // Inert OTP/auth lead (merchant API): no thread, no agent — it must not read as "Agente IA".
  const isApiHandled = lead.handledBy === "api";
  // Whether an AI agent can answer at all. Deliberately INDEPENDENT of employeeId:
  // an asesor being assigned does not mean the AI stopped. It keeps answering when the
  // agent used its own transfer tool (functions/chat/conversation.ts passes
  // inactivateAi: false), when an appointment pinned the asesor who will attend
  // (logic/appointment/chat.ts sets employeeId and never touches aiActive), and on the
  // lead-queue and broadcast paths. deriveHandledBy maps any employeeId to "human", so
  // all of those read as human-assigned while the AI is still live — which is exactly
  // the pair this section has to say out loud rather than leave to inference.
  //
  // A lead on a line with NO agent is the other direction: it renders as "Agente IA"
  // today but nothing will ever answer for it (inbound reaches the graph only when
  // there is an agent AND aiActive), so it gets no state line and no off switch.
  const hasAiAgent = !isApiHandled && Boolean(lead.assignedAgentId);
  // `conversations.get` defaults the flag to true for rows written before it existed.
  const isAiActive = hasAiAgent && Boolean(conversation?.aiActive);
  // The AI block states the AI. The owner block above it names a human or the API — or
  // stands in as today's bare "Agente IA" for an agent-less lead that has neither.
  const showsOwnerBlock = isHumanAssigned || isApiHandled || !hasAiAgent;

  const positionNameById = new Map(allPositions.map((p) => [p.id, p.displayName]));
  // Ticket statuses are per-pipeline; on a pipeline without tickets this map is
  // empty and the raw status id shows instead.
  const ticketStatusNameById = new Map(ticketPositions.map((p) => [p.status, p.displayName]));

  const currentPosition = positions.find((p) => p.id === lead.pipelinePositionId);
  const currentMovable = currentPosition ? MOVABLE_STAGES.has(currentPosition.stage) : false;
  // The "new"-stage positions (Nuevo/Entregado/Leído/Respondido) are WhatsApp
  // delivery facts, so they stay locked while an AI agent owns the lead — it is
  // what moves the lead out of them, and a manual move would just be overridden.
  // Without an agent nothing ever will, so the lock becomes a permanent trap:
  // those leads are the one case allowed to leave a WhatsApp position by hand.
  const aiOwnsLead = Boolean(lead.assignedAgentId);
  const canChangeEtapa = currentMovable || !aiOwnsLead;
  // Destinations never include a WhatsApp position — no one hand-sets "Entregado" — nor a
  // fianza position for anyone outside Palomma, whose move issues a certificate in Palomma's
  // name. Dropped from the option set rather than disabled: the etapa a lead is already in
  // still renders through SelectValue, so a lead sitting in a fianza position reads normally
  // and can still be moved out of it.
  const movablePositions = positions.filter(
    (p) =>
      MOVABLE_STAGES.has(p.stage) &&
      canMoveLeadToPipelinePosition({ email, pipelinePositionId: p.id })
  );

  const priceLabel =
    profile.price?.min !== undefined && profile.price?.max !== undefined
      ? `${formatCOP(profile.price.min)} – ${formatCOP(profile.price.max)}`
      : profile.price?.max !== undefined
        ? `Hasta ${formatCOP(profile.price.max)}`
        : profile.price?.min !== undefined
          ? `Desde ${formatCOP(profile.price.min)}`
          : undefined;

  const areaLabel =
    profile.sqMeters &&
    [profile.sqMeters.min, profile.sqMeters.max].some((value) => value !== undefined)
      ? `${[profile.sqMeters.min, profile.sqMeters.max]
          .filter((value) => value !== undefined)
          .join(" – ")} m²`
      : undefined;

  const handleCopy = () => {
    if (contact.kind === "none") return;
    // The bare handle, not the "@" form — "@handle" does not resolve in WhatsApp search.
    navigator.clipboard?.writeText(contact.copyValue);
    toast.success(`${contact.shortLabel} copiado`);
  };

  const handleCopyEmail = () => {
    if (!lead.email) return;
    navigator.clipboard?.writeText(lead.email);
    toast.success("Email copiado");
  };

  const handleCopyLink = () => {
    // Both containers now keep the URL in sync with the open lead — ?lead= on the
    // tablero, ?id= on the inbox — so the current location IS the shareable deep
    // link on either surface.
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Enlace copiado", {
      description: "Compártelo para que otros abran este lead.",
    });
    posthog.capture("lead link copied", { lead_id: lead.id, surface });
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-neutral-100/30">
      <div className="flex h-20 shrink-0 items-center gap-3 border-b border-neutral-300 bg-surface px-4">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-semibold text-white"
          style={{ background: colorFromName(displayName) }}
        >
          {initials(displayName)}
        </div>
        <div className="min-w-0 flex-1">
          <LeadNameEditor lead={lead} merchantId={merchantId} displayName={displayName} />
          {contact.kind === "none" ? (
            <div className="flex items-center gap-1 text-xs italic text-neutral-600">
              <AlertCircle className="size-3 shrink-0" />
              Sin teléfono ni usuario de WhatsApp
            </div>
          ) : (
            /* No handle icon here: contactDisplay already renders a username as
               "@handle", and an AtSign next to it read as a doubled "@". */
            <div className="truncate text-xs text-neutral-600">{contact.value}</div>
          )}
        </div>
        {contact.kind !== "none" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            title={`Copiar ${contact.shortLabel.toLowerCase()}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={handleCopyLink} title="Copiar enlace al lead">
          <Link2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="space-y-3 p-3">
        <Section title="Embudo y etapa">
          {/* The select's whole option set already comes from the LEAD's pipeline
              (LeadConversationPanel resolves it), and an etapa only means something
              inside one funnel — the cross-pipeline inbox puts "Entregado" and
              "Solicitud de Mantenimiento" in the same list with nothing to separate
              them. Naming the embudo here says which funnel the lead is in and, with
              it, why these are the options.

              Stated as text, never a control: there is no cross-pipeline move —
              moveInputSchema (logic/leads.ts) carries only a pipelinePositionId — so a
              disabled select would read as a permission the user is missing rather
              than as a fact. Shown for every lead including the default "Comercial":
              hiding it there would make its absence mean two different things. */}
          <p className="mb-2 text-sm text-neutral-900">
            {lead.pipelineDisplayName ?? lead.pipelineId}
          </p>
          <Select
            value={lead.pipelinePositionId}
            onValueChange={onChangeEtapa}
            disabled={!canChangeEtapa || isMoving}
          >
            <SelectTrigger
              className="w-full bg-surface"
              title={
                canChangeEtapa
                  ? undefined
                  : "Esta etapa la maneja WhatsApp — no se puede mover manualmente"
              }
            >
              <SelectValue>
                {currentPosition?.displayName ??
                  lead.pipelinePositionDisplayName ??
                  lead.pipelinePositionId}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {movablePositions.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Section>

        <Section title="Etiquetas">
          <LeadTagsEditor lead={lead} merchantId={merchantId} />
        </Section>

        <Section title="Notas">
          <LeadNotesSection merchantId={merchantId} leadId={lead.id} notes={notes} />
        </Section>

        <Section title="Manejado por">
          {showsOwnerBlock && (
            <>
              <div className="flex items-start gap-2">
                {isHumanAssigned ? (
                  <UserCog className="mt-0.5 size-4 shrink-0 text-neutral-600" />
                ) : isApiHandled ? (
                  <Webhook className="mt-0.5 size-4 shrink-0 text-neutral-600" />
                ) : (
                  <Bot className="mt-0.5 size-4 shrink-0 text-neutral-600" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="break-words text-sm font-semibold text-neutral-900">
                    {isHumanAssigned
                      ? (lead.assignmentRationale?.employeeName ?? "Asesor asignado")
                      : isApiHandled
                        ? "API"
                        : "Agente IA"}
                  </div>
                  {/* The ruleset name is a supervisor's one-glance check on whether routing
                      fired correctly — it has to be readable without opening the accordion.
                      It sits next to the advisor because the judgement is about the pair:
                      a correct rule that picked the wrong person is still a bad assignment.
                      Stored since the rationale shipped (core/lead/types.ts) and exposed by
                      leads.get, it had simply never been rendered: the frozen text puts it in
                      block 0, the headline, and AssignmentRationaleDetail drops that block
                      expecting the caller to show it — which the chat pill does and this
                      panel did not. Absent for listing-owner and counterpart assignments,
                      which genuinely have no rule. */}
                  {isHumanAssigned && lead.assignmentRationale?.rulesetName && (
                    <div className="break-words text-xs text-neutral-600">
                      Regla · {lead.assignmentRationale.rulesetName}
                    </div>
                  )}
                </div>
              </div>

              {isHumanAssigned && lead.assignmentRationale?.text && (
                <AssignmentRationaleDetail
                  tone="neutral"
                  text={lead.assignmentRationale.text}
                  className="mt-1 border-t border-neutral-300 pt-1"
                />
              )}
            </>
          )}

          {/* Stated, never inferred. "Ana Restrepo" alone leaves whether the AI is still
              answering to a guess, and the guess is wrong on every path that hands over a
              lead without silencing the agent. The rule above it is the same one the old
              header got wrong from the other side: two badges that could disagree. */}
          {hasAiAgent && (
            <div
              className={cn(
                "flex items-start gap-2",
                showsOwnerBlock && "mt-3 border-t border-neutral-300 pt-3"
              )}
            >
              <Bot className="mt-0.5 size-4 shrink-0 text-neutral-600" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="break-words text-sm font-semibold text-neutral-900">
                    Agente IA
                  </span>
                  {/* The app's status pattern: an 8px dot plus a Spanish label on the
                      neutral chip, never colour carrying the meaning by itself. This is
                      the same chip the inbox rows two columns to the left already use for
                      the same fact — purple dot for the AI, muted for a human — so the
                      panel and the list finally say it the same way. The green/grey fills
                      it replaces were a second colour voice, and paired with the red
                      button below they put two saturated colours on one boolean. */}
                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-pill bg-muted px-2.5 py-0.5 text-[11px] font-medium tracking-wide text-palomma-secondary">
                    <span
                      aria-hidden="true"
                      className={cn(
                        "size-1.5 shrink-0 rounded-full",
                        isAiActive ? "bg-palomma-primary" : "bg-muted-foreground"
                      )}
                    />
                    {isAiActive ? "Activa" : "Inactiva"}
                  </span>
                </div>
                <div className="break-words text-xs text-neutral-600">
                  {isAiActive
                    ? "Responde automáticamente a esta conversación"
                    : isHumanAssigned
                      ? "No responde · contesta el asesor asignado"
                      : "No responde · nadie contesta automáticamente"}
                </div>
                {isAiActive && conversation && (
                  <AgenteIaControl
                    merchantId={merchantId}
                    leadId={lead.id}
                    conversationId={conversation.id}
                    surface={surface}
                  />
                )}
              </div>
            </div>
          )}
        </Section>

        <Section title="Información">
          <div className="space-y-2 text-sm">
            <MetaRow
              label="Origen"
              value={
                <span className="rounded-pill bg-palomma-primary/10 px-2 py-0.5 text-xs text-palomma-primary">
                  {fuente ?? "—"}
                </span>
              }
            />
            {lead.email && (
              <div className="flex items-start justify-between gap-3">
                <span className="shrink-0 text-neutral-600">Email</span>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  title="Copiar email"
                  className="group inline-flex min-w-0 items-center gap-1.5 text-right text-neutral-900 hover:text-palomma-primary"
                >
                  <Mail className="size-3.5 shrink-0 text-neutral-600 group-hover:text-palomma-primary" />
                  <span className="min-w-0 break-all">{lead.email}</span>
                </button>
              </div>
            )}
            {lead.campaignName && <MetaRow label="Campaña" value={lead.campaignName} />}
            <MetaRow label="Creado" value={dayjs(lead.createdAt).format("DD/MM/YYYY HH:mm")} />
            <MetaRow label="Mensajes del cliente" value={lead.customerMessageCount ?? 0} />
          </div>
        </Section>

        {lead.aiAgentComments && (
          <Section title="Comentarios del agente IA">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-900">
              {lead.aiAgentComments}
            </p>
          </Section>
        )}

        {canRequestRentGuarantee && (
          <Section title="Fianza">
            <RentGuaranteeRequestForm leadId={lead.id} merchantId={merchantId} />
          </Section>
        )}

        {hasRentGuarantee && rentGuarantee && (
          <Section title="Fianza">
            <div className="space-y-3">
              <Row
                Icon={ShieldCheck}
                label="Autorización de habeas data"
                value={
                  rentGuarantee.habeasDataConfirmed
                    ? "✓ Confirmada por el usuario"
                    : "✗ Sin confirmar"
                }
              />
              {rentGuarantee.documentNumber && (
                <Row
                  Icon={FileText}
                  // Names the actual document when we captured which one it is. "Cédula" alone
                  // would keep calling a pasaporte a cédula, which is what the approval document
                  // used to do too.
                  label={
                    rentGuarantee.documentType
                      ? DOCUMENT_TYPE_LABELS[rentGuarantee.documentType]
                      : "Documento"
                  }
                  value={rentGuarantee.documentNumber}
                />
              )}
              {rentGuarantee.income !== undefined && (
                <Row
                  Icon={Wallet}
                  label="Ingresos del hogar (mensuales)"
                  value={formatCOP(rentGuarantee.income)}
                />
              )}
              {rentGuarantee.incomeAssessment && (
                <Row
                  Icon={DollarSign}
                  // Names the canon's source: the listing's code, or "solicitado" for the figure
                  // an asesor typed — which is not a listing price and must not read as one.
                  label={
                    rentGuarantee.incomeAssessment.canonSource === "advisor"
                      ? "Ingresos vs canon solicitado"
                      : rentGuarantee.incomeAssessment.canonSource === "tenant"
                        ? "Ingresos vs canon declarado"
                        : `Ingresos vs canon (${rentGuarantee.incomeAssessment.propertyCode})`
                  }
                  // Never colour alone: the ratio and the verdict are both spelled out, so
                  // the meaning survives for anyone who cannot distinguish the two states.
                  value={`${rentGuarantee.incomeAssessment.ratio}x sobre ${formatCOP(
                    rentGuarantee.incomeAssessment.monthlyRent
                  )} — ${
                    rentGuarantee.incomeAssessment.meetsRequirement
                      ? "✓ cumple el mínimo"
                      : "✗ no cumple el mínimo"
                  }`}
                />
              )}
              {rentGuarantee.requestedAt && (
                <Row
                  Icon={Clock}
                  label="Estudio solicitado"
                  value={dayjs(rentGuarantee.requestedAt).format("DD/MM/YYYY HH:mm")}
                />
              )}
              {rentGuarantee.requestedBy && (
                <Row Icon={UserCog} label="Solicitado por" value={rentGuarantee.requestedBy} />
              )}
              {/* Palomma-only, by the fianza gate rather than hasPalommaOnlyAccess — that flag
                  carries the configuration allowlist secret, which must never hand out the
                  fianza. Offered only while the lead is in the fianza funnel. */}
              {canManageRentGuarantee(email) && isInRentGuaranteeFunnel(lead.pipelinePositionId) && (
                <RentGuaranteeCosignersEditor
                  leadId={lead.id}
                  merchantId={merchantId}
                  stored={rentGuarantee.cosigners ?? []}
                />
              )}
              <RentGuaranteeCertificateBlock leadId={lead.id} kind="preApproved" />
              <RentGuaranteeCertificateBlock leadId={lead.id} kind="approved" />
              <RentGuaranteeRegenerateBlock lead={lead} />
            </div>
          </Section>
        )}

        <Section title="Preferencias del lead">
          {!hasProfile ? (
            <p className="text-sm italic text-neutral-600">Aún no se capturaron preferencias.</p>
          ) : (
            <div className="space-y-3">
              {profile.listingType && (
                <Row
                  Icon={Home}
                  label="Tipo de negocio"
                  value={LISTING_TYPE_LABELS[profile.listingType] ?? profile.listingType}
                />
              )}
              {profile.propertyTypes && profile.propertyTypes.length > 0 && (
                <Row Icon={Home} label="Tipo de inmueble" value={profile.propertyTypes.join(", ")} />
              )}
              {(lead.enquiredListing || profile.propertyCode) && (
                <EnquiredListingRow
                  listing={lead.enquiredListing}
                  fallbackCode={profile.propertyCode}
                />
              )}
              {profile.cities && profile.cities.length > 0 && (
                <Row Icon={MapPin} label="Ciudades" value={profile.cities.join(", ")} />
              )}
              {profile.neighborhoods && profile.neighborhoods.length > 0 && (
                <Row Icon={MapIcon} label="Barrios" value={profile.neighborhoods.join(", ")} />
              )}
              {profile.bedrooms !== undefined && (
                <Row Icon={Bed} label="Habitaciones" value={profile.bedrooms} />
              )}
              {profile.bathrooms !== undefined && (
                <Row Icon={Bath} label="Baños" value={profile.bathrooms} />
              )}
              {profile.garages !== undefined && (
                <Row Icon={Car} label="Garajes" value={profile.garages} />
              )}
              {areaLabel && <Row Icon={Ruler} label="Área" value={areaLabel} />}
              {priceLabel && <Row Icon={DollarSign} label="Precio" value={priceLabel} />}
              {profile.socialStratum && profile.socialStratum.length > 0 && (
                <Row Icon={Layers} label="Estrato" value={profile.socialStratum.join(", ")} />
              )}
              {profile.daysUntilMoveIn !== undefined && (
                <Row
                  Icon={Calendar}
                  label="Días para mudarse"
                  value={`${profile.daysUntilMoveIn} días`}
                />
              )}
              {profile.isPetFriendly !== undefined && (
                <Row Icon={PawPrint} label="Mascotas" value={profile.isPetFriendly ? "Sí" : "No"} />
              )}
            </div>
          )}
        </Section>

        {searchedCodes.length > 0 && (
          <Section title="Inmuebles consultados">
            <div className="flex items-start gap-3">
              <Eye className="mt-0.5 h-4 w-4 shrink-0 text-neutral-600" />
              <Chips values={searchedCodes} />
            </div>
          </Section>
        )}

        {appointments.length > 0 && (
          <Section title="Citas agendadas">
            <ul className="space-y-1 text-sm">
              {appointments.map((a) => {
                const inmueble =
                  a.listingCode && a.listingCode !== "NONE"
                    ? ` · Inmueble ${a.listingCode}`
                    : a.projectCode
                      ? ` · Proyecto ${a.projectCode}`
                      : "";
                const statusNote = a.status ? APPOINTMENT_STATUS_NOTES[a.status] : undefined;
                return (
                  <li key={a.id} className="text-neutral-900">
                    {a.date} · {a.time}
                    {inmueble}
                    {a.employeeName ? ` · ${a.employeeName}` : ""}
                    {statusNote && <span className="text-neutral-600"> ({statusNote})</span>}
                  </li>
                );
              })}
            </ul>
          </Section>
        )}

        {solicitudes.length > 0 && (
          <Section title="Solicitudes">
            <ul className="space-y-2 text-sm">
              {solicitudes.map((s) => (
                <li key={s.id}>
                  <div className="text-neutral-900">
                    {s.category || "Sin categoría"}
                    {s.description ? ` · ${s.description}` : ""}
                  </div>
                  <div className="text-xs text-neutral-600">
                    {ticketStatusNameById.get(s.status) ?? s.status} ·{" "}
                    {dayjs(s.createdAt).format("DD/MM/YYYY HH:mm")}
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {activities.length > 0 && (
          <Section title="Actividades">
            {/* Each entry gets its own page-wash recess inside the section's white
                surface, so one long chronological list reads as discrete events
                rather than a paragraph. A tint plus an icon does the separating —
                not a nested card, and not a second colour voice.

                Deliberately one line per event: the assignment rationale accordion
                belongs to "Manejado por" above, which answers "who owns this now and
                why" — the question actually asked when the drawer opens. This list
                answers "how did it get here". Rendering the accordion in both put the
                same text on screen twice for the common case of a lead assigned once.
                Note the two are not the same field: Lead.assignmentRationale skips
                sticky_rule/lead_pinned causes on purpose (logic/assignmentRationale.ts),
                so it holds the original decision those reuses point back to. */}
            <ul className="space-y-1.5 text-sm">
              {activities.map((activity) => {
                const Icon = activityIcon(activity.type);
                return (
                  <li key={activity.id} className="rounded-md bg-neutral-100/70 px-3 py-2">
                    <div className="flex gap-2.5">
                      <Icon className="mt-0.5 size-4 shrink-0 text-neutral-600" />
                      <div className="min-w-0 flex-1">
                        <div className="break-words text-neutral-900">
                          {stripRuleSegment(activityLabel(activity, positionNameById))}
                        </div>
                        <div className="text-xs text-neutral-600">
                          {dayjs(activity.createdAt).format("DD/MM/YYYY HH:mm")}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Section>
        )}

        {crmSyncLogs.length > 0 && (
          <Section title="Sincronización CRM">
            <ul className="space-y-2 text-sm">
              {crmSyncLogs.map((log) => (
                <CrmLogItem key={log.id} log={log} />
              ))}
            </ul>
          </Section>
        )}

        {/* Captación and Llamadas close the panel because they are the two sections a
            given merchant most often does not have at all. Both are gated on data
            rather than on a merchant flag: a rentals merchant with no voice product
            returns an empty calls list, and a lead that never asked to list a property
            returns no requests, so neither section renders and the panel is unchanged
            for everyone else. */}
        {listingRequests.length > 0 && (
          <Section title="Captación">
            <ul className="space-y-2 text-sm">
              {listingRequests.map((request, index) => (
                <CaptacionItem key={request.id} request={request} index={index} />
              ))}
            </ul>
          </Section>
        )}

        {calls.length > 0 && (
          <Section title="Llamadas">
            <ul className="space-y-2 text-sm">
              {calls.map((call) => (
                <CallItem key={call.id} call={call} merchantId={merchantId} />
              ))}
            </ul>
          </Section>
        )}
      </div>
    </div>
  );
};

export default PerfilPanel;
